/**
 * Mark days you cannot work.
 *
 * A month grid rather than a list, because the question people are answering is
 * "which weekend am I away", and that is a spatial question.
 *
 * The notice window is the interesting part. Days inside it are visibly
 * disabled AND carry the reason, rather than being silently unresponsive. The
 * server enforces the same rule with a 422; the client just refuses to let
 * somebody tap something that is going to be rejected, and says why before they
 * try.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarX, ChevronLeft, ChevronRight, Lock } from 'lucide-react';
import { api, ApiError, qk, type UnavailabilityRecord } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import {
  DAY_SHORT,
  diffDays,
  fmtDateLong,
  today,
  type IsoDate,
} from '../../lib/time.ts';
import { ErrorState, Field, LoadingBlock, Spinner } from '../../components/Bits.tsx';
import { OutcomeModal, Sheet } from '../../components/Overlay.tsx';

const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** The Monday-first grid for a month, padded to whole weeks. */
function monthGrid(year: number, month: number): IsoDate[] {
  const first = new Date(Date.UTC(year, month, 1));
  const lead = (first.getUTCDay() + 6) % 7;
  const start = new Date(first.getTime() - lead * 86_400_000);
  return Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * 86_400_000).toISOString().slice(0, 10));
}

export default function UnavailabilityPage() {
  const { tenant } = useSession();
  const slug = tenant?.tenantSlug ?? '';
  const qc = useQueryClient();

  const now = new Date();
  const [cursor, setCursor] = useState({ year: now.getFullYear(), month: now.getMonth() });
  const [picked, setPicked] = useState<{ date: IsoDate; existing: UnavailabilityRecord | null } | null>(null);
  const [note, setNote] = useState('');
  const [outcome, setOutcome] = useState<{ ok: boolean; title: string; message: string } | null>(null);

  const days = useMemo(() => monthGrid(cursor.year, cursor.month), [cursor]);
  const from = days[0] ?? today();
  const to = days[days.length - 1] ?? today();

  /**
   * The notice window, from the staff-safe settings slice.
   *
   * Boss-gated `GET /settings` would 403 here, so this asks the staff endpoint
   * and treats its absence as "unknown" rather than as zero. That distinction
   * matters: zero would pretend every day is fair game and let somebody tap a
   * day the server is about to refuse, which is exactly the silent rejection
   * this screen exists to avoid. Unknown leaves the calendar open, warns in the
   * sheet, and lets the server's own 422 do the explaining.
   */
  const settings = useQuery({
    queryKey: qk.mySettings(slug),
    queryFn: () => api.mySettings(slug),
    enabled: slug !== '',
    retry: false,
  });
  const noticeDays: number | null = settings.data?.unavailabilityNoticeDays ?? null;

  const query = useQuery({
    queryKey: qk.availability(slug, from, to),
    queryFn: () => api.availability(slug, from, to),
    enabled: slug !== '',
  });

  const marked = useMemo(() => {
    const m = new Map<string, UnavailabilityRecord>();
    for (const r of query.data?.items ?? []) m.set(r.date, r);
    return m;
  }, [query.data?.items]);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['availability', slug] });

  const mark = useMutation({
    mutationFn: (v: { date: IsoDate; note: string }) => api.markUnavailable(slug, { date: v.date, note: v.note || undefined }),
    onSuccess: () => {
      invalidate();
      setPicked(null);
      setNote('');
    },
    onError: (err) => {
      setPicked(null);
      const e = err instanceof ApiError ? err : null;
      setOutcome({
        ok: false,
        title: e?.code === 'notice_window' ? 'That day is too close' : 'Could not save that',
        message: e?.message ?? 'Something went wrong. Nothing was changed.',
      });
    },
  });

  const clear = useMutation({
    mutationFn: (id: string) => api.clearUnavailable(slug, id),
    onSuccess: () => {
      invalidate();
      setPicked(null);
    },
    onError: (err) => {
      setPicked(null);
      setOutcome({
        ok: false,
        title: 'Could not clear that day',
        message: err instanceof ApiError ? err.message : 'Something went wrong. Nothing was changed.',
      });
    },
  });

  if (query.isLoading) return <LoadingBlock label="Loading your days" />;
  if (query.isError) {
    return <ErrorState error={query.error} what="your unavailable days" onRetry={() => void query.refetch()} />;
  }

  const todayIso = today();
  /**
   * A day is locked when it is closer than the notice window.
   *
   * An unknown window falls back to zero, not to "no rule": zero still locks
   * today and everything behind it, which is the one part of this that is never
   * negotiable. What an unknown window must not do is grey out FUTURE days on a
   * guess, so the calendar stays open past today and the server's 422 does the
   * explaining if a day turns out to be too close.
   */
  const insideWindow = (d: IsoDate) => diffDays(d, todayIso) < (noticeDays ?? 0);

  return (
    <div className="px-4 py-4">
      <header className="mb-4">
        <h1 className="text-[20px] font-extrabold tracking-tight text-ink">Days off</h1>
        <p className="text-[13px] leading-relaxed text-muted">
          Tap a day to tell your manager you cannot work it.
          {noticeDays !== null && noticeDays > 0
            ? ` They need at least ${noticeDays} ${noticeDays === 1 ? 'day' : 'days'} notice, so anything sooner is locked.`
            : ' Your manager may need a few days notice, and you will be told if a day is too close.'}
        </p>
      </header>

      <div className="card overflow-hidden">
        <div className="flex items-center gap-2 px-3 py-2.5" style={{ borderBottom: '1px solid var(--sp-hairline)' }}>
          <button
            type="button"
            className="btn btn-icon btn-sm"
            onClick={() =>
              setCursor((c) => (c.month === 0 ? { year: c.year - 1, month: 11 } : { ...c, month: c.month - 1 }))
            }
            title="Previous month"
            aria-label="Previous month"
          >
            <ChevronLeft size={15} aria-hidden="true" />
          </button>
          <h2 className="flex-1 text-center text-[14px] font-bold text-ink">
            {MONTHS_LONG[cursor.month]} {cursor.year}
          </h2>
          <button
            type="button"
            className="btn btn-icon btn-sm"
            onClick={() =>
              setCursor((c) => (c.month === 11 ? { year: c.year + 1, month: 0 } : { ...c, month: c.month + 1 }))
            }
            title="Next month"
            aria-label="Next month"
          >
            <ChevronRight size={15} aria-hidden="true" />
          </button>
        </div>

        <div className="grid grid-cols-7 px-2 pt-2">
          {DAY_SHORT.map((d) => (
            <span key={d} className="pb-1 text-center text-[10.5px] font-bold uppercase tracking-wide text-faint">
              {d.slice(0, 1)}
            </span>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1 p-2">
          {days.map((d) => {
            const inMonth = Number(d.slice(5, 7)) === cursor.month + 1;
            const record = marked.get(d);
            const locked = insideWindow(d);
            const isToday = d === todayIso;
            return (
              <button
                key={d}
                type="button"
                disabled={locked}
                onClick={() => {
                  setPicked({ date: d, existing: record ?? null });
                  setNote(record?.note ?? '');
                }}
                className="cal-day relative flex aspect-square flex-col items-center justify-center text-[13.5px] font-bold"
                style={{
                  borderRadius: 'var(--sp-radius-sm)',
                  // A marked day is a solid coral tile. The old soft wash read
                  // as "selected"; this reads as "gone".
                  background: record ? 'var(--sp-warn)' : 'transparent',
                  border: record
                    ? '1px solid var(--sp-warn-strong)'
                    : isToday
                      ? '2px solid var(--sp-accent)'
                      : '1px solid transparent',
                  color: record
                    ? 'var(--sp-on-solid)'
                    : !inMonth
                      ? 'var(--sp-text-faint)'
                      : locked
                        ? 'var(--sp-text-faint)'
                        : 'var(--sp-text)',
                  boxShadow: record ? 'var(--sp-shadow-2)' : undefined,
                  opacity: locked ? 0.45 : 1,
                  cursor: locked ? 'not-allowed' : 'pointer',
                }}
                data-tip={
                  locked
                    ? `Too close to change|Your manager needs ${noticeDays ?? 0} ${
                        noticeDays === 1 ? 'day' : 'days'
                      } notice. Talk to them directly about this one.`
                    : record
                      ? `Marked unavailable|${record.note ? record.note : 'Tap to change or clear it.'}`
                      : 'Tap to mark unavailable|Your manager sees it on the roster straight away.'
                }
                aria-label={`${fmtDateLong(d)}${record ? ', marked unavailable' : ''}${locked ? ', too close to change' : ''}`}
              >
                {Number(d.slice(8))}
                {locked && inMonth ? (
                  <Lock size={9} aria-hidden="true" className="absolute bottom-1" />
                ) : record ? (
                  <CalendarX size={9} aria-hidden="true" className="absolute bottom-1" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 px-1 text-[11.5px] font-semibold text-faint">
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--sp-warn)' }} />
          Unavailable
        </span>
        <span className="flex items-center gap-1.5">
          <span aria-hidden="true" style={{ width: 10, height: 10, borderRadius: 3, border: '2px solid var(--sp-accent)' }} />
          Today
        </span>
        <span className="flex items-center gap-1.5">
          <Lock size={10} aria-hidden="true" />
          Too close to change
        </span>
      </div>

      <p className="mt-3 px-1 text-[12px] leading-relaxed text-faint">
        Marking a day does not cancel a shift you already have. If you are already rostered, tell your manager as
        well.
      </p>

      <Sheet
        open={picked !== null}
        onClose={() => setPicked(null)}
        title={picked ? fmtDateLong(picked.date) : ''}
        subtitle={picked?.existing ? 'You have marked this day unavailable.' : 'Tell your manager you cannot work.'}
        footer={
          picked?.existing ? (
            <button
              type="button"
              className="btn btn-lg w-full"
              onClick={() => picked.existing && clear.mutate(picked.existing.id)}
              disabled={clear.isPending}
            >
              {clear.isPending ? <Spinner size={16} label="Clearing" /> : null}
              I can work this day after all
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary btn-lg w-full"
              onClick={() => picked && mark.mutate({ date: picked.date, note })}
              disabled={mark.isPending}
            >
              {mark.isPending ? <Spinner size={16} label="Saving" /> : <CalendarX size={16} aria-hidden="true" />}
              Mark unavailable
            </button>
          )
        }
      >
        <Field label="Note for your manager" id="unavail-note" hint="Optional, and they can see it on the roster.">
          <textarea
            id="unavail-note"
            className="field"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Add a reason (optional)"
          />
        </Field>
        {picked && noticeDays !== null && diffDays(picked.date, todayIso) < noticeDays + 3 && !insideWindow(picked.date) ? (
          <p className="text-[12.5px] leading-relaxed" style={{ color: 'var(--sp-warn-strong)' }}>
            This is close to the {noticeDays} day cutoff. If the roster for that week is already out, message your
            manager too.
          </p>
        ) : null}
      </Sheet>

      <OutcomeModal
        open={outcome !== null}
        onClose={() => setOutcome(null)}
        ok={outcome?.ok ?? true}
        title={outcome?.title ?? ''}
        message={outcome?.message ?? ''}
      />
    </div>
  );
}
